// Hangi bağlamda çalışıyoruz?
//
// Üç yüzey var ve bunlar birbirinin küçültülmüş hâli DEĞİL:
//   phone    dar ekran, başparmak, tek elle.
//   car      geniş ama ALÇAK ekran, kol mesafesi, titreşim, dokunmatik.
//            Araç ekranında klavyeyle uzun metin yazmak fiilen imkânsızdır;
//            arayüz bu işleri telefona devreder.
//   desktop  fare/klavye, oturarak.
//
// Yalnız genişliğe bakmak yetmiyor: 1366×768 bir dizüstü de "geniş ve alçak".
// Ayırt edici şey dokunmatik olması. `maxTouchPoints` dizüstünde 0'dır.
//
// Ölçüm yanılabileceği için elle geçiş de var: `?surface=car` adres parametresi
// ya da `localStorage['akis.surface']`. Ekran görüntüsü almak ve gerçek cihaz
// olmadan doğrulamak için gereken bu.

export type Surface = 'phone' | 'car' | 'desktop';

const KEY = 'akis.surface';
// Linux masaüstü gibi görünen tarayıcıda gerçek bir dokunuş görüldüğünde yazılır.
const TOUCH_SEEN_KEY = 'akis.touch-seen';
const VALID: Surface[] = ['phone', 'car', 'desktop'];

let current: Surface = 'desktop';
const listeners = new Set<(s: Surface) => void>();

function override(): Surface | null {
  const fromUrl = new URLSearchParams(location.search).get('surface');
  const mapped = fromUrl && (VALID as string[]).includes(fromUrl) ? (fromUrl as Surface) : null;
  if (mapped) {
    try { localStorage.setItem(KEY, mapped); } catch { /* depolama kapalı */ }
    return mapped;
  }
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && (VALID as string[]).includes(saved)) return saved as Surface;
  } catch { /* depolama kapalı */ }
  return null;
}

/**
 * Kendini sıradan masaüstü Linux Chrome olarak tanıtan tarayıcı.
 *
 * Güncel Tesla tarayıcısı böyledir: kullanıcı aracısı
 * `X11; Linux x86_64 … Chrome/148` biçimindedir ve eski sürümlerdeki "Tesla"
 * ibaresini taşımaz (canlı erişim kaydında görüldü, 3 Ekim 2026). Yalnız ada
 * bakmak aracı bilgisayar sayıyordu.
 */
function linuxDesktopUa(): boolean {
  const ua = navigator.userAgent;
  return /X11;[^)]*Linux/.test(ua) && !/Android|CrOS/.test(ua);
}

function touchSeen(): boolean {
  try { return localStorage.getItem(TOUCH_SEEN_KEY) === '1'; } catch { return false; }
}

/** Fare yerine parmakla kullanılan ekran: dokunma noktası var, birincil işaretçi
 *  kaba ya da üzerine gelme (hover) yok; ya da daha önce gerçek dokunuş görüldü. */
function touchDriven(): boolean {
  const media = (query: string) => { try { return window.matchMedia(query).matches; } catch { return false; } };
  return navigator.maxTouchPoints > 0 || media('(pointer: coarse)') || media('(hover: none)') || touchSeen();
}

/** Tesla tarayıcısı: adında yazıyor ya da dokunmatik kullanılan Linux masaüstü
 *  tarayıcısı. Tam ekran yolu ve cihaz adı gibi Tesla'ya özgü davranışlar buna bakar. */
export function isTeslaLike(): boolean {
  return /tesla/i.test(navigator.userAgent) || (linuxDesktopUa() && touchDriven());
}

function measure(): Surface {
  const forced = override();
  if (forced) return forced;

  const w = window.innerWidth;
  const h = window.innerHeight;
  const touch = navigator.maxTouchPoints > 0;
  const tesla = isTeslaLike();

  if (w <= 700) return 'phone';
  // Yatay tutulan telefon da "geniş + alçak + dokunmatik"tir (844×390) ama araç
  // değildir: oynatıcı zaten "çevirin" diyor ve çevirince sürüş uyarısı çıkıyor,
  // kaynak formu "telefonunuzdan tamamlayın"a dönüyordu. Yalnız kendini telefon
  // diye tanıtan tarayıcı dışlanır (Android "Mobile", iPhone/iPod); tablet ve
  // araç panelleri bu işareti taşımaz. Tesla her zaman araçtır.
  if (!tesla && touch && /Android.+Mobile|iPhone|iPod/i.test(navigator.userAgent)) return 'phone';
  // Geniş + alçak + dokunmatik = araç/TV dokunmatik paneli.
  if (tesla || (touch && h <= 900 && w / h >= 1.25)) return 'car';
  return 'desktop';
}

/** Yüzeyi ölçer, `<html data-surface>` olarak yazar ve değiştiyse haber verir. */
export function refreshSurface(): Surface {
  const next = measure();
  if (next === current && document.documentElement.dataset.surface === next) return current;
  current = next;
  document.documentElement.dataset.surface = next;
  for (const listener of listeners) listener(next);
  return next;
}

export function startSurfaceWatch(): void {
  refreshSurface();
  // Dokunmatik olduğunu önceden bildirmeyen araç tarayıcısı için: Linux masaüstü
  // gibi görünen tarayıcıda ilk gerçek dokunuşta araç düzenine geçilir ve bu
  // hatırlanır. Fareyle kullanılan Linux bilgisayarı etkilenmez.
  if (linuxDesktopUa() && !touchSeen()) {
    const learn = (event: Event) => {
      if (event instanceof PointerEvent && event.pointerType !== 'touch') return;
      try { localStorage.setItem(TOUCH_SEEN_KEY, '1'); } catch { /* depolama kapalı */ }
      window.removeEventListener('pointerdown', learn, true);
      window.removeEventListener('touchstart', learn, true);
      refreshSurface();
    };
    window.addEventListener('pointerdown', learn, true);
    window.addEventListener('touchstart', learn, true);
  }
  let timer: number | null = null;
  const debounced = () => {
    if (timer) clearTimeout(timer);
    timer = window.setTimeout(() => refreshSurface(), 150);
  };
  window.addEventListener('resize', debounced);
  window.addEventListener('orientationchange', debounced);
}

export function surface(): Surface {
  return current;
}

export function isCar(): boolean { return current === 'car'; }
export function isPhone(): boolean { return current === 'phone'; }
/** Araç ve telefon: dokunmatik. Kalıcı geri düğmesi, büyük hedefler. */
export function isTouchSurface(): boolean { return current !== 'desktop'; }

export function onSurfaceChange(listener: (s: Surface) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
